import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import {
  ShieldCheck, Zap, Percent, MessageCircle, Lock, ArrowRight, CheckCircle2,
  CreditCard, Phone, Send, Banknote,
} from "lucide-react";

const BLACK = "#000000";
const GREEN = "#00A651";
const YELLOW = "#FFCC00";

function useFont() {
  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap";
    document.head.appendChild(l);
    return () => { document.head.removeChild(l); };
  }, []);
}

const fmtFcfa = (n) => `${Math.round(n).toLocaleString("fr-FR")} FCFA`;
const fmtEur = (n) => `${Number(n).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export default function PayLanding() {
  useFont();
  const [params] = useSearchParams();
  const agent = params.get("agent") || "";
  const [cfg, setCfg] = useState({ fcfa_per_eur: 635, fee_rate: 0.02, min_eur: 5, max_eur: 2000 });
  const [montant, setMontant] = useState(100);
  const [form, setForm] = useState({ nom: "", email: "", tel: "", num_beneficiaire: "", reseau: "OM", pays: "Cameroun" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.get("annule")) toast.info("Paiement annulé. Tu peux réessayer quand tu veux.");
    api.get("/pay/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const recu = (Number(montant) || 0) * cfg.fcfa_per_eur;
  const total = (Number(montant) || 0) * (1 + cfg.fee_rate);
  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const scrollToForm = () => document.getElementById("pay-form")?.scrollIntoView({ behavior: "smooth" });

  const pay = async () => {
    const m = Number(montant);
    if (!m || m < cfg.min_eur || m > cfg.max_eur) return toast.error(`Montant entre ${cfg.min_eur} € et ${cfg.max_eur} €`);
    if (!form.nom.trim() || !form.email.trim() || !form.tel.trim() || !form.num_beneficiaire.trim()) return toast.error("Remplis tous les champs");
    setLoading(true);
    try {
      const { data } = await api.post("/pay/checkout", {
        ...form, montant_eur: m, agent: agent || undefined, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e)); setLoading(false); }
  };

  return (
    <div data-testid="pay-landing" className="min-h-screen bg-black text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
      {/* NAV */}
      <header className="sticky top-0 z-30 bg-black/90 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-black text-lg">
            <span style={{ color: GREEN }}>BentSkin</span><span style={{ color: YELLOW }}>Go Pay</span>
          </div>
          <button data-testid="nav-cta" onClick={scrollToForm} className="h-10 px-5 rounded-full font-bold text-black" style={{ background: GREEN }}>Envoyer</button>
        </div>
      </header>

      {/* SECTION 1 — HERO */}
      <section className="max-w-5xl mx-auto px-5 pt-14 pb-10 text-center">
        <div className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold mb-6" style={{ background: "rgba(0,166,81,0.15)", color: GREEN }}>
          <ShieldCheck className="w-4 h-4" /> Compte Pro sécurisé · Reçu en 5min
        </div>
        <div className="flex items-center justify-center gap-3 text-5xl sm:text-6xl mb-6">
          <span>🇧🇪</span><span>🇫🇷</span>
          <ArrowRight className="w-8 h-8" style={{ color: YELLOW }} />
          <span>🇨🇲</span>
        </div>
        <h1 className="font-black text-3xl sm:text-5xl leading-tight tracking-tight">
          Envoie de l'argent au Cameroun en <span style={{ color: GREEN }}>5min</span>
        </h1>
        <p className="mt-4 text-white/70 text-base sm:text-lg max-w-xl mx-auto">
          Frais 2% seulement. Compte Pro sécurisé. Depuis l'Europe vers Orange Money & MoMo, 24/7.
        </p>
        <button data-testid="hero-cta" onClick={scrollToForm} className="mt-8 h-14 px-8 rounded-full font-extrabold text-lg text-black inline-flex items-center gap-2 hover:scale-105 transition-transform" style={{ background: YELLOW }}>
          Envoyer maintenant <ArrowRight className="w-5 h-5" />
        </button>
        <div className="mt-6 flex items-center justify-center gap-6 text-sm text-white/60">
          <span className="flex items-center gap-1.5"><Zap className="w-4 h-4" style={{ color: GREEN }} /> 5min</span>
          <span className="flex items-center gap-1.5"><Percent className="w-4 h-4" style={{ color: GREEN }} /> 2% de frais</span>
          <span className="flex items-center gap-1.5"><Lock className="w-4 h-4" style={{ color: GREEN }} /> Stripe</span>
        </div>
      </section>

      {/* SECTION 2 — SIMULATEUR */}
      <section className="max-w-5xl mx-auto px-5 py-10">
        <div className="rounded-3xl p-6 sm:p-8 border border-white/10" style={{ background: "#0d0d0d" }}>
          <h2 className="font-black text-2xl text-center">Calcule ce que ton proche reçoit</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-5 items-center">
            <div>
              <label className="text-sm text-white/60 font-semibold">Tu envoies</label>
              <div className="mt-2 relative">
                <input data-testid="sim-amount" type="number" min={cfg.min_eur} max={cfg.max_eur} value={montant}
                  onChange={(e) => setMontant(e.target.value)}
                  className="w-full h-16 rounded-2xl bg-black border-2 border-white/15 focus:border-[#00A651] outline-none px-5 text-3xl font-black text-white" />
                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-2xl font-black text-white/50">€</span>
              </div>
              <p className="mt-2 text-xs text-white/40">Frais 2% : total à payer <span className="text-white/70 font-semibold" data-testid="sim-total">{fmtEur(total)}</span></p>
            </div>
            <div className="rounded-2xl p-5 text-center" style={{ background: "rgba(0,166,81,0.12)" }}>
              <div className="text-sm text-white/60 font-semibold">Ton proche reçoit</div>
              <div data-testid="sim-receive" className="mt-1 font-black text-3xl sm:text-4xl" style={{ color: GREEN }}>{fmtFcfa(recu)}</div>
              <div className="mt-1 text-xs text-white/40">Taux : 100 € = {fmtFcfa(cfg.fcfa_per_eur * 100)}</div>
            </div>
          </div>
          <button data-testid="sim-cta" onClick={scrollToForm} className="mt-6 w-full h-14 rounded-2xl font-extrabold text-lg text-black" style={{ background: GREEN }}>Envoyer maintenant</button>
        </div>
      </section>

      {/* SECTION 3 — COMMENT ÇA MARCHE */}
      <section className="max-w-5xl mx-auto px-5 py-10">
        <h2 className="font-black text-2xl text-center mb-8">Comment ça marche</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {[
            { icon: CreditCard, t: "Paie sur Stripe", d: "Règle en carte, 100% sécurisé." },
            { icon: Phone, t: "Donne le numéro OM/MoMo", d: "Le numéro de ton proche au Cameroun." },
            { icon: CheckCircle2, t: "Reçu en 5min", d: "Argent envoyé + capture preuve." },
          ].map((s, i) => (
            <div key={i} data-testid={`step-${i}`} className="rounded-2xl p-6 border border-white/10 bg-[#0d0d0d]">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: YELLOW }}>
                <s.icon className="w-6 h-6 text-black" />
              </div>
              <div className="text-sm font-black" style={{ color: YELLOW }}>Étape {i + 1}</div>
              <div className="font-bold text-lg mt-1">{s.t}</div>
              <p className="text-white/60 text-sm mt-1">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 4 — POURQUOI NOUS */}
      <section className="max-w-5xl mx-auto px-5 py-10">
        <h2 className="font-black text-2xl text-center mb-8">Pourquoi nous</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { icon: ShieldCheck, t: "Compte Pro Asso" },
            { icon: Zap, t: "5min garanti" },
            { icon: Percent, t: "2% de frais" },
            { icon: MessageCircle, t: "Preuve WhatsApp" },
          ].map((w, i) => (
            <div key={i} data-testid={`why-${i}`} className="rounded-2xl p-5 text-center border border-white/10 bg-[#0d0d0d]">
              <w.icon className="w-7 h-7 mx-auto" style={{ color: GREEN }} />
              <div className="mt-3 font-bold text-sm">{w.t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 5 — FORMULAIRE PAIEMENT */}
      <section id="pay-form" className="max-w-2xl mx-auto px-5 py-12">
        <div className="rounded-3xl p-6 sm:p-8 border-2" style={{ borderColor: GREEN, background: "#0d0d0d" }}>
          <h2 className="font-black text-2xl text-center">Envoie ton argent maintenant</h2>
          <p className="text-center text-white/60 text-sm mt-1">Remplis les infos, paie en sécurité avec Stripe.</p>
          {agent && <p className="text-center text-xs mt-2" style={{ color: YELLOW }}>Agent : {agent}</p>}

          <div className="mt-6 space-y-3">
            <input data-testid="pay-nom" value={form.nom} onChange={setF("nom")} placeholder="Ton nom complet" className="w-full h-13 py-3.5 rounded-xl bg-black border-2 border-white/15 focus:border-[#00A651] outline-none px-4 text-white placeholder-white/40" />
            <input data-testid="pay-email" type="email" value={form.email} onChange={setF("email")} placeholder="Ton email" className="w-full py-3.5 rounded-xl bg-black border-2 border-white/15 focus:border-[#00A651] outline-none px-4 text-white placeholder-white/40" />
            <input data-testid="pay-tel" value={form.tel} onChange={setF("tel")} placeholder="Ton téléphone (WhatsApp)" className="w-full py-3.5 rounded-xl bg-black border-2 border-white/15 focus:border-[#00A651] outline-none px-4 text-white placeholder-white/40" />
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <input data-testid="pay-montant" type="number" min={cfg.min_eur} max={cfg.max_eur} value={montant} onChange={(e) => setMontant(e.target.value)} placeholder="Montant" className="w-full py-3.5 rounded-xl bg-black border-2 border-white/15 focus:border-[#00A651] outline-none px-4 text-white" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-bold text-white/50">€</span>
              </div>
              <select data-testid="pay-reseau" value={form.reseau} onChange={setF("reseau")} className="w-full py-3.5 rounded-xl bg-black border-2 border-white/15 focus:border-[#00A651] outline-none px-4 text-white">
                <option value="OM">Orange Money</option>
                <option value="MoMo">MTN MoMo</option>
              </select>
            </div>
            <input data-testid="pay-beneficiaire" value={form.num_beneficiaire} onChange={setF("num_beneficiaire")} placeholder="N° du bénéficiaire (OM/MoMo)" className="w-full py-3.5 rounded-xl bg-black border-2 border-white/15 focus:border-[#00A651] outline-none px-4 text-white placeholder-white/40" />
            <input data-testid="pay-pays" value={form.pays} onChange={setF("pays")} placeholder="Pays" className="w-full py-3.5 rounded-xl bg-black border-2 border-white/15 focus:border-[#00A651] outline-none px-4 text-white placeholder-white/40" />
          </div>

          <div className="mt-5 rounded-xl p-4 flex items-center justify-between" style={{ background: "rgba(255,204,0,0.12)" }}>
            <div className="text-sm text-white/70">Ton proche reçoit</div>
            <div className="font-black text-xl" style={{ color: GREEN }}>{fmtFcfa(recu)}</div>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-white/50 px-1">
            <span>Total à payer (avec 2% de frais)</span><span className="font-semibold text-white/80">{fmtEur(total)}</span>
          </div>

          <button data-testid="pay-submit" onClick={pay} disabled={loading} className="mt-5 w-full h-14 rounded-2xl font-extrabold text-lg text-black inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: GREEN }}>
            <Lock className="w-5 h-5" /> {loading ? "Redirection..." : "Payer en sécurité avec Stripe"}
          </button>
          <p className="mt-3 text-center text-xs text-white/40 flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> Paiement chiffré via Stripe · Carte test 4242 4242 4242 4242</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 mt-6">
        <div className="max-w-5xl mx-auto px-5 py-8 text-center text-white/50 text-sm space-y-3">
          <div className="font-black text-base"><span style={{ color: GREEN }}>BentSkin</span> <span style={{ color: YELLOW }}>Go Pay</span></div>
          <p className="max-w-2xl mx-auto text-xs leading-relaxed">
            BentSkin Go Pay facilite l'envoi d'argent vers le Cameroun via Orange Money et MTN MoMo.
            Service opéré par un compte pro associatif. En utilisant ce service, tu acceptes nos conditions.
            Les délais de 5 minutes sont indicatifs et dépendent des opérateurs mobiles.
          </p>
          <p className="text-xs">Contact : support via WhatsApp après paiement.</p>
          <p className="text-xs text-white/30">© 2026 BentSkin Go Pay. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
}
